import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ModalController } from '@ionic/angular';
import { ClientConfigService } from 'src/app/core/client/client-config.service';
import { ApiService } from 'src/app/services/api.service';
import { UtilService } from 'src/app/services/util/util.service';
import { KonfirmasiPrimaryComponent } from 'src/app/shared/state/konfirmasi-primary/konfirmasi-primary.component';

@Component({
  selector: 'app-permohonan-kredit-modal',
  templateUrl: './permohonan-kredit-modal.component.html',
  styleUrls: ['./permohonan-kredit-modal.component.scss'],
})
export class PermohonanKreditModalComponent implements OnInit {
  @Input() submissionLoan: any;
  @Input() submissionTenor: any;
  @Input() submissionTenorType: any;
  @Input() leadId: any;
  @Input() checkingDataType: any;
  @Input() existingDetail: any;
  @Input() pengajuan: any;
  @Input() ringkasan: any;
  @Input() noSurat: any;

  form: FormGroup;
  loading = false;
  isAutoCheckingData = false;
  nominalPattern = /^\d{1,3}(\.\d{3})*$/;

  constructor(
    private fb: FormBuilder,
    private modalController: ModalController,
    private clientConfig: ClientConfigService,
    private api: ApiService,
    public util: UtilService,
  ) {}

  ngOnInit() {
    this.isAutoCheckingData = this.normalizeCheckingDataType(this.checkingDataType) === 'otomatis';
    if (!this.clientConfig.isClient('BAV')) {
      this.isAutoCheckingData = false;
    }

    const detail = this.existingDetail || {};
    const pengajuan = this.pengajuan || {};
    const ringkasan = this.ringkasan || {};
    const noSuratValue = this.getInitialValue(this.pickFirstValue(this.noSurat, detail?.nomorSurat));
    const tanggalCetakSuratValue = this.getTanggalCetakSuratValue(detail);
    const pokokPinjamanValue = this.getInitialByType(this.pickFirstValue(this.submissionLoan, detail?.pokok_pinjaman, detail?.pokokPinjaman), 'number');
    const sukuBungaValue = this.getInitialByType(
      this.pickFirstValue(
        detail?.suku_bunga,
        detail?.sukuBunga,
        ringkasan?.interest_rate,
      ),
      'string',
    );
    const jangkaWaktuValue = this.getInitialByType(this.pickFirstValue(`${this.submissionTenor} ${this.submissionTenorType}`, detail?.jangka_waktu, detail?.jangkaWaktu), 'string');
    const angsuranValue = this.util.normalizeNominalValue(
      this.pickFirstValue(detail?.angsuran, ringkasan?.angsuran),
    );
    const blokirTabunganValue = this.getInitialByType(
      this.pickFirstValue(
        detail?.blokir_tabungan,
        detail?.blokirTabungan,
        pengajuan?.blokir_tabungan,
      ),
      'string',
    );
    const provisiValue = this.getInitialByType(
      this.pickFirstValue(detail?.provisi, pengajuan?.provisi),
      'string',
    );
    const dendaValue = this.getInitialByType(
      this.pickFirstValue(detail?.denda, pengajuan?.denda),
      'string',
    );
    const sukuBungaNormalized = this.normalizeDecimalString(sukuBungaValue);
    const blokirTabunganNormalized = this.normalizeDecimalString(blokirTabunganValue) || '0';
    const provisiNormalized = this.normalizeDecimalString(provisiValue) || '0';
    const dendaNormalized = this.normalizeDecimalString(dendaValue) || '0';
    const biayaAsuransiValue = this.util.normalizeNominalValue(
      this.pickFirstValue(
        detail?.biaya_asuransi,
        detail?.biayaAsuransi,
        pengajuan?.biaya_asuransi,
      ),
    );
    const biayaCekSertifikatValue = this.util.normalizeNominalValue(
      this.pickFirstValue(
        detail?.biaya_cek_sertfikat,
        detail?.biayaCekSertifikat,
        pengajuan?.biaya_cek_sertfikat,
      ),
    );
    const biayaAphtValue = this.util.normalizeNominalValue(
      this.pickFirstValue(detail?.biaya_apht, detail?.biayaApht, pengajuan?.biaya_apht),
    );
    const biayaSkmHtValue = this.util.normalizeNominalValue(
      this.pickFirstValue(detail?.biaya_skm_ht, detail?.biayaSkmHt, pengajuan?.biaya_skm_ht),
    );
    const biayaFiduciaValue = this.util.normalizeNominalValue(
      this.pickFirstValue(detail?.biaya_fiducia, detail?.biayaFiducia, pengajuan?.biaya_fiducia),
    );
    const biayaLegasisasiValue = this.util.normalizeNominalValue(
      this.pickFirstValue(detail?.biaya_legasisasi, detail?.biayaLegasisasi, pengajuan?.biaya_legasisasi),
    );
    const waarmekingValue = this.util.normalizeNominalValue(
      this.pickFirstValue(detail?.waarmeking, detail?.biayaWaarmeking, pengajuan?.waarmeking),
    );

    const angsuranDisplay = this.util.formatIntegerForDisplay(angsuranValue);
    const biayaAsuransiDisplay = this.util.formatIntegerForDisplay(biayaAsuransiValue);
    const biayaCekSertifikatDisplay = this.util.formatIntegerForDisplay(biayaCekSertifikatValue);
    const biayaAphtDisplay = this.util.formatIntegerForDisplay(biayaAphtValue);
    const biayaSkmHtDisplay = this.util.formatIntegerForDisplay(biayaSkmHtValue);
    const biayaFiduciaDisplay = this.util.formatIntegerForDisplay(biayaFiduciaValue);
    const biayaLegasisasiDisplay = this.util.formatIntegerForDisplay(biayaLegasisasiValue);
    const waarmekingDisplay = this.util.formatIntegerForDisplay(waarmekingValue);

    this.form = this.fb.group({
      no_surat: [{ value: noSuratValue, disabled: this.isAutoCheckingData }, Validators.required],
      tanggal_cetak_surat: [tanggalCetakSuratValue, Validators.required],
      pokok_pinjaman: [{ value: pokokPinjamanValue, disabled: true }, Validators.required],
      suku_bunga: [
        sukuBungaNormalized,
        [Validators.required, Validators.pattern(/^\d+(\.\d+)?$/)],
      ],
      jangka_waktu: [{ value: jangkaWaktuValue, disabled: true }, Validators.required],
      angsuran: [angsuranDisplay, [Validators.required, Validators.pattern(this.nominalPattern)]],
      blokir_tabungan: [
        blokirTabunganNormalized,
        [Validators.required, Validators.pattern(/^\d+(\.\d+)?$/)],
      ],
      provisi: [
        provisiNormalized,
        [Validators.required, Validators.pattern(/^\d+(\.\d+)?$/)],
      ],
      denda: [
        dendaNormalized,
        [Validators.required, Validators.pattern(/^\d+(\.\d+)?$/)],
      ],
      biaya_asuransi: [biayaAsuransiDisplay, [Validators.required, Validators.pattern(this.nominalPattern)]],
      biaya_cek_sertfikat: [biayaCekSertifikatDisplay, [Validators.required, Validators.pattern(this.nominalPattern)]],
      biaya_apht: [biayaAphtDisplay, [Validators.required, Validators.pattern(this.nominalPattern)]],
      biaya_skm_ht: [biayaSkmHtDisplay, [Validators.required, Validators.pattern(this.nominalPattern)]],
      biaya_fiducia: [biayaFiduciaDisplay, [Validators.required, Validators.pattern(this.nominalPattern)]],
      biaya_legasisasi: [biayaLegasisasiDisplay, [Validators.required, Validators.pattern(this.nominalPattern)]],
      waarmeking: [waarmekingDisplay, [Validators.required, Validators.pattern(this.nominalPattern)]],
    });

    if (this.isAutoCheckingData) {
      this.disableAutoModeControls();
    }
  }

  close() {
    this.modalController.dismiss();
  }

  onNumberInput(event: any, controlName: string) {
    if (this.isAutoCheckingData) {
      return;
    }
    const rawValue = event?.detail?.value ?? event?.target?.value ?? '';
    const digits = rawValue.toString().replace(/\D+/g, '');
    const formattedValue = digits === '' ? '' : this.util.formatIntegerForDisplay(Number(digits));
    this.form?.get(controlName)?.setValue(formattedValue, { emitEvent: false });
    try {
      const el = event?.target || event?.detail?.target;
      if (el) {
        el.value = formattedValue;
      }
    } catch (e) {}
  }

  onDecimalInput(event: any, controlName: string) {
    if (this.isAutoCheckingData) {
      return;
    }
    const rawValue = event?.detail?.value ?? event?.target?.value ?? '';
    let normalized = rawValue.toString().replace(/,/g, '.').replace(/[^0-9.]/g, '');
    const firstDotIndex = normalized.indexOf('.');
    if (firstDotIndex !== -1) {
      const beforeDot = normalized.slice(0, firstDotIndex + 1);
      const afterDot = normalized.slice(firstDotIndex + 1).replace(/\./g, '');
      normalized = `${beforeDot}${afterDot}`;
    }
    const numericValue = normalized === '' ? null : Number(normalized);
    this.form?.get(controlName)?.setValue(numericValue, { emitEvent: false });
    try {
      const el = event?.target || event?.detail?.target;
      if (el) {
        el.value = this.util.formatDecimalForDisplay(normalized);
      }
    } catch (e) {}
  }

  onPercentInput(event: any, controlName: string) {
    if (this.isAutoCheckingData) {
      return;
    }
    const rawValue = event?.detail?.value ?? event?.target?.value ?? '';
    const normalized = this.normalizeDecimalString(rawValue);
    this.form?.get(controlName)?.setValue(normalized, { emitEvent: false });
    try {
      const el = event?.target || event?.detail?.target;
      if (el) {
        el.value = normalized;
      }
    } catch (e) {}
  }

  async submit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.util.showToast('Lengkapi form terlebih dahulu', 'danger', 'bottom');
      return;
    }
    document.body.classList.add('modal-confirm-open');
    const modal = await this.modalController.create({
      component: KonfirmasiPrimaryComponent,
      cssClass: 'modalConfirmation',
      componentProps: {
        image: 'assets/imgs/konfirmasi-hapus.svg',
        title: 'Konfirmasi Permohonan',
        titlePrimaryButton: 'Ya, Lanjutkan',
        titleSecondaryButton: 'Batal',
        desc: 'Apakah Anda yakin ingin melanjutkan permohonan kredit?',
        button: '2',
      },
    });
    modal.onDidDismiss().then((data: any) => {
      document.body.classList.remove('modal-confirm-open');
      if (data.role === 'confirm') {
        this.submitRequest();
      }
    });
    return modal.present();
  }

  private submitRequest() {
    if (this.loading) {
      return;
    }
    this.loading = true;
    const payload = this.sanitizePayload(this.form.getRawValue());
    this.api.post(`backoffice/credit-analyst/checking-data/${this.leadId}`, payload)
      .subscribe(
        (data: any) => {
          this.loading = false;
          if (data?.error === 0 || data?.status === 200) {
            this.util.showToast(data?.message || 'Permohonan berhasil dibuat', 'success', 'bottom');
            this.modalController.dismiss({ data: payload }, 'confirm');
          } else {
            this.util.showToast(data?.message || 'Gagal membuat permohonan', 'danger', 'bottom');
          }
        },
        (error) => {
          this.loading = false;
          console.log('error', error);
          this.util.showToast(
            error?.error?.error_message ||
              error?.error?.message ||
              'Terjadi kesalahan',
            'danger',
            'bottom',
          );
        },
      );
  }

  isDashValue(controlName: string): boolean {
    return this.form?.get(controlName)?.value === '-';
  }

  private sanitizePayload(payload: any): any {
    const numericFields = [
      'pokok_pinjaman',
      'angsuran',
      'biaya_asuransi',
      'biaya_cek_sertfikat',
      'biaya_apht',
      'biaya_skm_ht',
      'biaya_fiducia',
      'biaya_legasisasi',
      'waarmeking',
    ];

    const sanitizedPayload = { ...payload };
    for (const field of numericFields) {
      sanitizedPayload[field] = this.util.normalizeNominalValue(sanitizedPayload[field]);
    }

    sanitizedPayload.suku_bunga = this.normalizeDecimalNumber(sanitizedPayload.suku_bunga);
    sanitizedPayload.blokir_tabungan = this.normalizeDecimalNumber(sanitizedPayload.blokir_tabungan);
    sanitizedPayload.provisi = this.normalizeDecimalNumber(sanitizedPayload.provisi);
    sanitizedPayload.denda = this.normalizeDecimalNumber(sanitizedPayload.denda);

    if (!this.isAutoCheckingData) {
      return sanitizedPayload;
    }

    const numericFieldSet = new Set(numericFields);
    Object.keys(sanitizedPayload).forEach((key) => {
      const value = sanitizedPayload[key];
      if (this.isEmptyValue(value) || String(value).trim() === '-') {
        sanitizedPayload[key] = numericFieldSet.has(key) ? 0 : null;
      }
    });

    return sanitizedPayload;
  }

  private normalizeDecimalString(value: any): string {
    if (this.isEmptyValue(value) || String(value).trim() === '-') {
      return '';
    }

    let normalized = String(value)
      .trim()
      .replace(/%/g, '')
      .replace(/,/g, '.')
      .replace(/[^0-9.]/g, '');

    const firstDotIndex = normalized.indexOf('.');
    if (firstDotIndex !== -1) {
      const beforeDot = normalized.slice(0, firstDotIndex + 1);
      const afterDot = normalized.slice(firstDotIndex + 1).replace(/\./g, '');
      normalized = `${beforeDot}${afterDot}`;
    }

    return normalized;
  }

  private normalizeDecimalNumber(value: any): number {
    const normalized = this.normalizeDecimalString(value);
    if (normalized === '') {
      return 0;
    }

    const parsed = Number(normalized);
    if (Number.isNaN(parsed)) {
      return 0;
    }

    return parsed;
  }

  private getTanggalCetakSuratValue(detail: any): string {
    const existingDate = this.pickFirstValue(
      detail?.tanggal_cetak_surat,
      detail?.tanggalCetakSurat,
    );

    if (!this.isAutoCheckingData) {
      return this.getInitialValue(existingDate);
    }

    if (!this.isEmptyValue(existingDate) && String(existingDate).trim() !== '-') {
      return String(existingDate).trim();
    }

    return this.getTodayDateString();
  }

  private getTodayDateString(): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private getInitialValue(value: any): any {
    return this.isEmptyValue(value) ? '' : value;
  }

  private getInitialByType(value: any, type: 'number' | 'string'): any {
    if (type === 'number') {
      return this.isEmptyValue(value) || String(value).trim() === '-' ? 0 : value;
    }
    // string type
    return this.isEmptyValue(value) ? '-' : value;
  }

  private pickFirstValue(...values: any[]): any {
    for (const value of values) {
      if (!this.isEmptyValue(value)) {
        return value;
      }
    }
    return '';
  }

  private isEmptyValue(value: any): boolean {
    return value === null || value === undefined || String(value).trim() === '';
  }

  private normalizeCheckingDataType(value: any): string {
    return String(value || '').trim().toLowerCase();
  }

  private disableAutoModeControls() {
    const controlsToDisable = [
      'tanggal_cetak_surat',
      'pokok_pinjaman',
      'suku_bunga',
      'jangka_waktu',
      'angsuran',
      'blokir_tabungan',
      'provisi',
      'denda',
      'biaya_asuransi',
      'biaya_cek_sertfikat',
      'biaya_apht',
      'biaya_skm_ht',
      'biaya_fiducia',
      'biaya_legasisasi',
      'waarmeking'
    ];

    controlsToDisable.forEach((controlName) => {
      this.form?.get(controlName)?.disable({ emitEvent: false });
    });
  }

}
